/**
 * SMS resources service — migrated to typed `@tiaki/http-client` per ADR 113.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface Resource {
  id: number;
  public_id: string;
  external_id?: string;
  name: string;
  type: string;
  school?: number;
  room?: number;
  building?: number;
  vendor_id?: string;
  icon_uri?: string;
  icon_url?: string;
  latitude?: string;
  longitude?: string;
  data?: Record<string, unknown>;
  event_id?: number;
  event_type?: string;
  created_at?: string;
  updated_at?: string;
  room_name?: string;
  room_id?: number;
}

export interface ResourceListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Resource[];
}

export interface ResourceFilters {
  room?: number;
  building?: number;
  school?: number;
  search?: string;
  page?: number;
  page_size?: number;
}
export interface ResourceCreate {
  name: string;
  type: string;
  school?: number;
  room?: number;
  id?: number;
}
export const createResourceCreateObj = (): ResourceCreate => ({
  name: '',
  type: '',
  school: undefined,
  room: undefined,
  id: undefined,
});

export interface ResourceUpdate {
  id: number;
  data: Partial<Omit<Resource, 'id' | 'public_id'>>;
}

export async function getResources(
  filters: ResourceFilters = {},
): Promise<ResourceListResponse> {
  const result = await client.GET('/api/v2/resource/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as ResourceListResponse;
}

export async function getResourceById(id: number): Promise<Resource> {
  const result = await client.GET('/api/v2/resource/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as Resource;
}

export async function createResource(
  data: Omit<Resource, 'id' | 'public_id'>,
): Promise<Resource> {
  const result = await client.POST('/api/v2/resource/', {
    body: data as never,
  });
  return unwrap(result) as unknown as Resource;
}

export async function updateResource(
  id: number,
  data: Partial<Omit<Resource, 'id' | 'public_id'>>,
): Promise<Resource> {
  const result = await client.PATCH('/api/v2/resource/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as Resource;
}

export async function deleteResource(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/resource/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}
